import type { Lead } from "./types";

export type LeadQualityBucket =
  | "high_confidence"
  | "needs_review"
  | "likely_bad_lead";

export type LeadQualityTone = "neutral" | "good" | "warn" | "bad";

export interface LeadQualitySummary {
  score: number;
  bucket: LeadQualityBucket;
  // Full-form label for popover / tooltip headers ("High Confidence").
  label: string;
  color: string;
  // Compact, user-friendly chip face. "Unverified" until an admin clicks
  // Verify; otherwise one of Verified / Review / Flagged. Numeric score
  // stays available via `score` for the popover.
  chipLabel: string;
  // Palette key the chip uses. Intentionally decoupled from `color` so
  // the pre-verify state can be neutral grey instead of the heuristic
  // bucket color (which misleadingly implied real quality data).
  chipTone: LeadQualityTone;
  isVerified: boolean;
}

const TONE_COLOR: Record<LeadQualityTone, string> = {
  neutral: "#94A3B8", // slate-400 — "we haven't verified yet"
  good: "#10B981",
  warn: "#F59E0B",
  bad: "#EF4444",
};

const BUCKET_TO_CHIP: Record<
  LeadQualityBucket,
  { chipLabel: string; chipTone: LeadQualityTone }
> = {
  high_confidence: { chipLabel: "Verified", chipTone: "good" },
  needs_review: { chipLabel: "Review", chipTone: "warn" },
  likely_bad_lead: { chipLabel: "Flagged", chipTone: "bad" },
};

function normalizeBucket(value?: string | null): LeadQualityBucket | null {
  const normalized = (value || "").trim().toLowerCase();
  if (normalized === "high_confidence") return "high_confidence";
  if (normalized === "needs_review") return "needs_review";
  if (normalized === "likely_bad_lead") return "likely_bad_lead";
  return null;
}

// MIRROR of backend scorer bands (backend/api/services/lead_verification.py
// HIGH_CONFIDENCE_THRESHOLD / NEEDS_REVIEW_THRESHOLD). The lockstep is
// pinned STRUCTURALLY from both sides: leadQuality.test.ts parses the
// backend file and backend tests/test_lead_intel_tier_consumers.py parses
// this one — a change to either side alone goes red in that side's suite.
export const HIGH_CONFIDENCE_THRESHOLD = 80;
export const NEEDS_REVIEW_THRESHOLD = 50;

function bucketFromScore(score: number): LeadQualityBucket {
  if (score >= HIGH_CONFIDENCE_THRESHOLD) return "high_confidence";
  if (score >= NEEDS_REVIEW_THRESHOLD) return "needs_review";
  return "likely_bad_lead";
}

// Scorer v2 intel tiers, persisted at verification_flags.intel_tier.
// Absent on rows scored before v2 — always optional, never an error.
export type LeadIntelTier =
  | "ULTRA_REAL"
  | "REAL"
  | "UNCERTAIN"
  | "LIKELY_FAKE"
  | "ULTRA_FAKE";

export const INTEL_TIERS: readonly LeadIntelTier[] = [
  "ULTRA_REAL",
  "REAL",
  "UNCERTAIN",
  "LIKELY_FAKE",
  "ULTRA_FAKE",
];

/**
 * PKT-0944a · ONE verdict. The lead's verdict IS the scorer-v2 intel tier
 * (verification_flags.intel_tier). Every surface — calendar, board, lead
 * list, lead detail, dashboard feed — and routing read it through these
 * helpers, never the label. MIRROR of backend/api/services/lead_verdict.py
 * (lockstep pinned by leadVerdict.test.ts). Null = not scored by v2 yet.
 */
export function leadVerdict(
  lead: { verification_flags?: unknown } | null | undefined
): LeadIntelTier | null {
  const flags = lead?.verification_flags;
  if (!flags || typeof flags !== "object") return null;
  const raw = (flags as Record<string, unknown>).intel_tier;
  const tier = typeof raw === "string" ? (raw.trim().toUpperCase() as LeadIntelTier) : null;
  return tier && INTEL_TIERS.includes(tier) ? tier : null;
}

/** Kept for existing callers; identical to leadVerdict. */
export function leadIntelTier(lead: Pick<Lead, "verification_flags">): LeadIntelTier | null {
  return leadVerdict(lead);
}

export function isFakeVerdict(lead: { verification_flags?: unknown } | null | undefined): boolean {
  const v = leadVerdict(lead);
  return v === "ULTRA_FAKE" || v === "LIKELY_FAKE";
}

/** True only for REAL / ULTRA_REAL — the gate client conversion (PKT-0944b) uses. */
export function isConvertibleVerdict(
  lead: { verification_flags?: unknown } | null | undefined
): boolean {
  const v = leadVerdict(lead);
  return v === "REAL" || v === "ULTRA_REAL";
}

/**
 * The score-band label, reconciled with the verdict (backend
 * lead_verdict.label_for_verdict): FAKE -> likely_bad_lead; UNCERTAIN never
 * high_confidence; REAL never likely_bad_lead. Rows stored before the fix
 * (until migration 132 runs) read consistently too.
 */
export function labelForVerdict(
  tier: LeadIntelTier | null,
  label: LeadQualityBucket | null
): LeadQualityBucket | null {
  if (tier === "ULTRA_FAKE" || tier === "LIKELY_FAKE") return "likely_bad_lead";
  if (tier === "UNCERTAIN" && label === "high_confidence") return "needs_review";
  if ((tier === "REAL" || tier === "ULTRA_REAL") && label === "likely_bad_lead") return "needs_review";
  return label;
}

/** The lead's label band as every surface should show it. */
export function leadQualityLabel(
  lead: { verification_flags?: unknown; verification_label?: string | null }
): LeadQualityBucket | null {
  return labelForVerdict(leadVerdict(lead), normalizeBucket(lead.verification_label));
}

function clampScore(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function fallbackScore(lead: Lead): number {
  const firstName = (lead.name || "").trim().split(/\s+/)[0]?.toLowerCase();
  const email = (lead.email || "").trim().toLowerCase();
  const phoneDigits = (lead.phone || "").replace(/\D/g, "");

  // v2 lockstep: mirrors the backend's presence weights (name 10, phone 15,
  // email 15+10 syntax, name-in-email 10). Presence alone must NOT reach the
  // 80 high_confidence band — the old 25/30/30/15 weights let a merely
  // complete form read "high confidence" client-side, the exact defect
  // scorer v2 removes server-side.
  let score = 0;
  if (firstName) score += 10;
  if (email) score += 25;
  if (phoneDigits.length >= 10) score += 15;
  if (firstName && email && email.includes(firstName)) score += 10;

  return clampScore(score);
}

export function deriveLeadQuality(lead: Lead): LeadQualitySummary {
  const persistedScore =
    typeof lead.verification_score === "number"
      ? clampScore(lead.verification_score)
      : null;
  const score = persistedScore ?? fallbackScore(lead);
  // PKT-0944a: the band never contradicts the verdict (FAKE -> Flagged,
  // UNCERTAIN never Verified, REAL never Flagged). Rows without a tier
  // behave exactly as before.
  const bucket: LeadQualityBucket =
    labelForVerdict(
      leadVerdict(lead),
      normalizeBucket(lead.verification_label) ?? bucketFromScore(score)
    ) ?? bucketFromScore(score);

  const bucketMeta: Record<LeadQualityBucket, { label: string; color: string }> = {
    high_confidence: { label: "High Confidence", color: "#10B981" },
    needs_review: { label: "Needs Review", color: "#F59E0B" },
    likely_bad_lead: { label: "Likely Bad Lead", color: "#EF4444" },
  };

  // A lead is only "verified" once a real vendor call has stamped the
  // row with a provider name. Heuristic-only rows stay neutral grey so
  // the UI doesn't overclaim.
  const isVerified = Boolean(
    (lead.verification_provider || "").trim().length > 0
  );

  const { chipLabel, chipTone } = isVerified
    ? BUCKET_TO_CHIP[bucket]
    : { chipLabel: "Unverified", chipTone: "neutral" as LeadQualityTone };

  return {
    score,
    bucket,
    label: bucketMeta[bucket].label,
    color: TONE_COLOR[chipTone],
    chipLabel,
    chipTone,
    isVerified,
  };
}

export function deriveLeadSourceLabel(lead: Lead): string {
  const platform = (lead.source_platform || "").trim();
  const channel = (lead.source_channel || "").trim();
  const utmSource = (lead.source_utm_source || "").trim();
  const referrerHost = (lead.source_referrer_host || "").trim();
  const legacySource = (lead.source || "").trim();

  if (platform) return platform;
  if (utmSource) return utmSource;
  if (channel) return channel;
  if (referrerHost) return referrerHost;
  if (legacySource) return legacySource;
  return "website";
}
